<script lang="ts">
    import { t } from '../stores/locale';
    import { theme } from '../stores/theme';
    import Icon from '../components/Icon.svelte';

    // ── Helpers ───────────────────────────────────────────────────────────────

    const now  = Date.now();
    const pts  = 289; // 24 h

    function mockHistory(base: number, noise: number, lo: number, hi: number, drift = 0): number[] {
        let v = Math.max(lo, Math.min(hi, base));
        return Array.from({ length: pts }, (_, i) => {
            v += (Math.random() - 0.5) * noise + drift * Math.sin(i / 15);
            if (Math.random() > 0.97) v += Math.random() * noise * 8;
            v = Math.max(lo, Math.min(hi, v));
            return Math.round(v * 10) / 10;
        });
    }

    // ── Material Colors ───────────────────────────────────────────────────────
    const M3_COLORS = {
        light: {
            error: '#D32F2F',
            warning: '#F57C00',
            success: '#2E7D32',
            primary: '#1976D2',
            surface: '#F7F2FA'
        },
        dark: {
            error: '#F2B8B5',
            warning: '#FFEE58',
            success: '#81C784',
            primary: '#90CAF9',
            surface: '#2B2930'
        }
    };

    $: colors = $theme === 'dark' ? M3_COLORS.dark : M3_COLORS.light;

    function getGaugeColor(pct: number, isTemp = false, _th = $theme): string {
        const limit = isTemp ? 100 : 85;
        const warn  = isTemp ? 75 : 65;
        const c = _th === 'dark' ? M3_COLORS.dark : M3_COLORS.light;
        if (pct > limit) return c.error;
        if (pct > warn)  return c.warning;
        return c.success;
    }

    function getVoltageColor(v: number, _th = $theme): string {
        const c = _th === 'dark' ? M3_COLORS.dark : M3_COLORS.light;
        if (v < 20.0 || v > 30.0) return c.error;
        if (v < 22.5 || v > 26.0) return c.warning;
        return c.success;
    }

    // ── Mock data ─────────────────────────────────────────────────────────────

    const cpuHistory  = mockHistory(25, 10, 5, 95, 2);
    const tempHistory = mockHistory(48, 6, 30, 110, 1.5);
    const ramHistory  = mockHistory(65, 3, 40, 95, 0.5);
    const voltHistory = mockHistory(24.2, 0.4, 18.0, 32.0, 0.1);

    const cpuNow  = cpuHistory[cpuHistory.length - 1];
    const tempNow = tempHistory[tempHistory.length - 1];
    const ramNow  = ramHistory[ramHistory.length - 1];
    const voltNow = voltHistory[voltHistory.length - 1];

    const recentPts = 60;
    const cpuRecent  = cpuHistory.slice(-recentPts);
    const tempRecent = tempHistory.slice(-recentPts);
    const ramRecent  = ramHistory.slice(-recentPts);
    const voltRecent = voltHistory.slice(-recentPts);

    const diskUsed  = 3.7;
    const diskTotal = 16;
    const diskPct   = Math.round((diskUsed / diskTotal) * 100);

    const uptimeSeconds = 9 * 3600 + 47 * 60 + 12;
    function fmtUptime(s: number): string {
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        const parts = [];
        if (d) parts.push(`${d}d`);
        if (h) parts.push(`${h}h`);
        parts.push(`${m}m`);
        return parts.join(' ');
    }

    const networkInterfaces = [
        { name: 'eth0', status: 'up', ip: '192.168.1.100', tx: '1.2 MB/s', rx: '450 KB/s' },
        { name: 'eth1', status: 'down', ip: '10.0.0.1 (Static)', tx: '0 B/s', rx: '0 B/s' },
    ];

    const uptime = {
        d: 31,
        h: 9,
        m: 14
    }

    const services = [
        { name: 'listmod-core', label: 'Core Engine', status: 'running' },
        { name: 'listmod-logger', label: 'Logging Service', status: 'running' },
        { name: 'listmod-licensing', label: 'Licensing Manager', status: 'running' },
        { name: 'listmod-modbus-tcp', label: 'Modbus TCP', status: 'running' },
        { name: 'listmod-modbus-rtu', label: 'Modbus Serial', status: 'stopped' },
        { name: 'listmod-api', label: 'REST API', status: 'running' },
    ];

    const backupInfo = {
        lastBackup: '2026-03-23 04:00:00',
        size: '14.2 MB',
        status: 'success'
    };

    // ── Sparkline SVG ─────────────────────────────────────────────────────────

    const SW = 400, SH = 100;

    function sparkPath(data: number[], scaleMax = 100, minOverride?: number): string {
        const min = minOverride !== undefined ? minOverride : 0;
        const range = scaleMax - min;
        const xS = (i: number) => (i / (data.length - 1)) * SW;
        const yS = (v: number) => SH * (1 - (v - min) / range);
        return data.map((v, i) => `${i === 0 ? 'M' : 'L'}${xS(i).toFixed(1)},${yS(v).toFixed(1)}`).join(' ');
    }

    function areaPath(data: number[], scaleMax = 100, minOverride?: number): string {
        const line = sparkPath(data, scaleMax, minOverride);
        return `${line} L${SW},${SH} L0,${SH} Z`;
    }

    // ── System info ───────────────────────────────────────────────────────────

    const sysInfo = [
        { key: 'system.infoHostname', value: 'listmod-hu-01' },
        { key: 'system.infoOS',       value: 'Debian GNU/Linux 12' },
        { key: 'system.infoKernel',   value: '6.1.0-28-arm64' },
        { key: 'system.infoFirmware', value: '2.4.1' },
        { key: 'system.infoArch',     value: 'ARM64' },
        { key: 'system.infoSerial',   value: '100000004d3b2c1a' },
    ];

    // ── Radial Disk Gauge Math ────────────────────────────────────────────────
    const R = 40;
    const C = 2 * Math.PI * R;
    $: dashOffset = C - (diskPct / 100) * C;
</script>

<div class="page">
    <h1>{$t('nav.system')}</h1>

    <!-- ── Stat cards ──────────────────────────────────────────────────────── -->
    <div class="stat-grid">

        <!-- CPU Card -->
        <div class="card stat-card">
            <div class="card-content">
                <span class="label">{$t('system.cpu')}</span>
                <span class="value large" style="color: {getGaugeColor(cpuNow, false, $theme)}">
                    {cpuNow}<span class="unit">%</span>
                </span>
            </div>
            <div class="card-immersive-graph immersive">
                <svg viewBox="0 0 {SW} {SH}" preserveAspectRatio="none" class="immersive-svg">
                    <defs>
                        <linearGradient id="grad-cpu" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stop-color={getGaugeColor(cpuNow, false, $theme)} stop-opacity={$theme === 'dark' ? '0.25' : '0.4'} />
                            <stop offset="100%" stop-color={getGaugeColor(cpuNow, false, $theme)} stop-opacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={areaPath(cpuRecent, 100)} fill="url(#grad-cpu)" />
                    <path d={sparkPath(cpuRecent, 100)} fill="none" stroke={getGaugeColor(cpuNow, false, $theme)} stroke-width="2.5" />
                </svg>
            </div>
        </div>

        <!-- Temp Card -->
        <div class="card stat-card">
            <div class="card-content">
                <span class="label">{$t('system.temp')}</span>
                <span class="value large" style="color: {getGaugeColor(tempNow, true, $theme)}">
                    {tempNow}<span class="unit">°C</span>
                </span>
            </div>
            <div class="card-immersive-graph immersive">
                <svg viewBox="0 0 {SW} {SH}" preserveAspectRatio="none" class="immersive-svg">
                    <defs>
                        <linearGradient id="grad-temp" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stop-color={getGaugeColor(tempNow, true, $theme)} stop-opacity={$theme === 'dark' ? '0.25' : '0.4'} />
                            <stop offset="100%" stop-color={getGaugeColor(tempNow, true, $theme)} stop-opacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={areaPath(tempRecent, 120)} fill="url(#grad-temp)" />
                    <path d={sparkPath(tempRecent, 120)} fill="none" stroke={getGaugeColor(tempNow, true, $theme)} stroke-width="2.5" />
                </svg>
            </div>
        </div>

        <!-- RAM Card -->
        <div class="card stat-card">
            <div class="card-content">
                <span class="label">{$t('system.ram')}</span>
                <span class="value large" style="color: {getGaugeColor(ramNow, false, $theme)}">
                    {ramNow}<span class="unit">%</span>
                </span>
            </div>
            <div class="card-immersive-graph immersive">
                <svg viewBox="0 0 {SW} {SH}" preserveAspectRatio="none" class="immersive-svg">
                    <defs>
                        <linearGradient id="grad-ram" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stop-color={getGaugeColor(ramNow, false, $theme)} stop-opacity={$theme === 'dark' ? '0.25' : '0.4'} />
                            <stop offset="100%" stop-color={getGaugeColor(ramNow, false, $theme)} stop-opacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={areaPath(ramRecent, 100)} fill="url(#grad-ram)" />
                    <path d={sparkPath(ramRecent, 100)} fill="none" stroke={getGaugeColor(ramNow, false, $theme)} stroke-width="2.5" />
                </svg>
            </div>
        </div>

        <!-- Power (Voltage) Card -->
        <div class="card stat-card">
            <div class="card-content">
                <span class="label">{$t('system.power')}</span>
                <span class="value large" style="color: {getVoltageColor(voltNow, $theme)}">
                    {voltNow.toFixed(1)}<span class="unit">V</span>
                </span>
            </div>
            <div class="card-immersive-graph immersive">
                <svg viewBox="0 0 {SW} {SH}" preserveAspectRatio="none" class="immersive-svg">
                    <defs>
                        <linearGradient id="grad-volt" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stop-color={getVoltageColor(voltNow, $theme)} stop-opacity={$theme === 'dark' ? '0.25' : '0.4'} />
                            <stop offset="100%" stop-color={getVoltageColor(voltNow, $theme)} stop-opacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={areaPath(voltRecent, 48)} fill="url(#grad-volt)" />
                    <path d={sparkPath(voltRecent, 48)} fill="none" stroke={getVoltageColor(voltNow, $theme)} stroke-width="2.5" />
                </svg>
            </div>
        </div>

        <!-- Uptime -->
        <div class="card uptime-card mini-card">
            <div class="card-content">
                <span class="label">{$t('system.uptime')}</span>
                <span class="value large">
                    {uptime.d}<span class="unit">d</span> 
                    {uptime.h}<span class="unit">h</span> 
                    {uptime.m}<span class="unit">m</span>
                </span>
                <span class="subtext">{$t('system.uptimeSince')} 2026-02-22 00:48</span>
            </div>
        </div>
        
        <!-- Disk -->
        <div class="card disk-card-modern stat-card">
            <div class="card-content">
                <span class="label">{$t('system.disk')}</span>
                <div class="disk-visual">
                    <svg viewBox="0 0 100 100" class="radial-gauge">
                        <circle class="gauge-track" cx="50" cy="50" r={R} 
                                style="stroke: {getGaugeColor(diskPct, false, $theme)}; opacity: {$theme === 'dark' ? 0.15 : 0.25}" />
                        <circle class="gauge-fill" cx="50" cy="50" r={R} 
                                style="stroke-dasharray: {C}; stroke-dashoffset: {dashOffset}; stroke: {getGaugeColor(diskPct, false, $theme)}" />
                    </svg>
                    <div class="disk-values">
                        <span class="value large" style="color: {getGaugeColor(diskPct, false, $theme)}">
                            {diskPct}<span class="unit">%</span>
                        </span>
                        <span class="subtext">{diskUsed} / {diskTotal} GB</span>
                    </div>
                </div>
            </div>
        </div>

    </div>

    <!-- ── Detailed Info Sections ─────────────────────────────────────────── -->
    <div class="details-grid">
        
        <!-- System Info -->
        <section class="card info-section immersive">
            <h2>{$t('system.info')}</h2>
            <div class="info-list">
                {#each sysInfo as row}
                    <div class="info-row">
                        <span class="info-label">{$t(row.key)}</span>
                        <span class="info-value mono">{row.value}</span>
                    </div>
                {/each}
            </div>
        </section>

        <!-- Network -->
        <section class="card info-section immersive">
            <h2>{$t('system.network')}</h2>
            <div class="info-list">
                {#each networkInterfaces as net}
                    <div class="info-row network-row">
                        <div class="net-header">
                            <span class="status-dot" class:active={net.status === 'up'}></span>
                            <span class="info-label">{net.name}</span>
                            <span class="info-value mono">{net.ip}</span>
                        </div>
                        {#if net.status === 'up'}
                            <div class="net-stats">
                                <span class="net-stat" title="Transmit">
                                    <Icon name="info" size={12} /> Tx: {net.tx}
                                </span>
                                <span class="net-stat" title="Receive">
                                    <Icon name="info" size={12} /> Rx: {net.rx}
                                </span>
                            </div>
                        {:else}
                            <span class="net-stats down">Disconnected</span>
                        {/if}
                    </div>
                {/each}
            </div>
        </section>

        <!-- Services -->
        <section class="card info-section immersive">
            <h2>{$t('system.services')}</h2>
            <div class="info-list">
                {#each services as srv}
                    <div class="info-row">
                        <div class="service-name">
                            <span class="info-label">{srv.label}</span>
                            <span class="service-id mono">{srv.name}</span>
                        </div>
                        <span class="service-badge" class:running={srv.status === 'running'}>
                            {srv.status}
                        </span>
                    </div>
                {/each}
            </div>
        </section>

        <!-- Backups -->
        <section class="card info-section immersive">
            <h2>{$t('system.backups')}</h2>
            <div class="info-list">
                <div class="info-row">
                    <span class="info-label">Last Backup</span>
                    <span class="info-value mono">{backupInfo.lastBackup}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Size</span>
                    <span class="info-value mono">{backupInfo.size}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Status</span>
                    <span class="service-badge running">Success</span>
                </div>
            </div>
        </section>

    </div>

</div>

<style lang="scss">
    // ── Stat grid ─────────────────────────────────────────────────────────────

    .stat-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: 1.5em;
        margin-bottom: 2em;
    }

    .card-content {
        padding: 1.5em;
        display: flex;
        flex-direction: column;
        gap: 0.2em;
        flex: 1;
        position: relative;
        z-index: 2;
    }

    .label {
        font-size: 0.75em;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: $color-text-secondary;
        font-variation-settings: "wdth" 95, "wght" 600, "opsz" 14;
    }

    .value {
        font-size: 1.5rem;
        font-weight: 600;
        color: $color-text;
        line-height: 1.2;
        font-variation-settings: "wdth" 100, "wght" 600, "opsz" 32;

        &.large {
            font-size: 2.25rem;
            margin-top: 0.1em;
            font-variation-settings: "wdth" 100, "wght" 700, "opsz" 48;
        }
    }

    .unit {
        font-size: 0.5em;
        font-weight: 500;
        margin-left: 0.1em;
        opacity: 0.8;
        font-variation-settings: "wdth" 100, "wght" 500, "opsz" 14;
    }

    .subtext {
        font-size: 0.8em;
        color: $color-disabled-text;
        margin-top: 0.25em;
        font-variation-settings: "wdth" 100, "wght" 400, "opsz" 14;
    }

    // ── Immersive Stat Card (Edge to Edge) ───────────────────────────────────

    .stat-card {
        min-height: 180px; // Increased from 160px
        position: relative;
        overflow: hidden;
    }

    .card-immersive-graph {
        position: absolute !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        height: 40% !important; // Reduced from 50%
        z-index: 1;
        pointer-events: none;
        padding: 0 !important; // CRITICAL: Bypass global padding

        .immersive-svg {
            width: 100% !important;
            height: 100% !important;
            display: block;
        }
    }

    // ── Modern Disk Card ─────────────────────────────────────────────────────

    .disk-card-modern {
        .disk-visual {
            display: flex;
            align-items: center;
            gap: 1.5em;
            margin-top: 0.5em;
        }

        .radial-gauge {
            width: 80px;
            height: 80px;
            transform: rotate(-90deg);
            flex-shrink: 0;
        }

        .gauge-track {
            fill: none;
            stroke-width: 8;
        }

        .gauge-fill {
            fill: none;
            stroke-width: 8;
            stroke-linecap: round;
            transition: stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .disk-values {
            display: flex;
            flex-direction: column;
            justify-content: center;
        }
    }

    .mini-card {
        min-height: 120px;
    }

    // ── Details Grid ─────────────────────────────────────────────────────────

    .details-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1.5em;

        @media (min-width: 1000px) {
            grid-template-columns: 1fr 1fr;
        }
    }

    .info-section {
        margin-bottom: 0;
    }

    .info-list {
        padding: 0.5em 0;
    }

    .info-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1em 1.5rem;
        position: relative;

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 1.5rem;
            right: 1.5rem;
            height: 1px;
            background-color: $color-border;
            opacity: 0.4;
        }

        &:last-child::after { display: none; }
        &:hover { background-color: $color-bg-offset; }
    }

    .info-label {
        font-size: 0.9em;
        color: $color-text-secondary;
        font-weight: 500;
    }

    .info-value {
        font-size: 0.9em;
        color: $color-text;
        font-weight: 500;

        &.mono {
            font-family: $font-family-mono;
            word-break: break-all;
        }
    }

    .network-row {
        flex-direction: column;
        align-items: stretch;
        gap: 0.75em;

        .net-header {
            display: flex;
            align-items: center;
            gap: 0.75em;
        }

        .status-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: $color-danger;
            &.active { background-color: $color-success; }
        }

        .net-stats {
            display: flex;
            gap: 1.5em;
            font-size: 0.85em;
            color: $color-text;
            background: rgba($color-border, 0.15);
            padding: 0.5em 1em;
            border-radius: 8px;
            font-family: $font-family-mono;

            &.down { color: $color-disabled-text; font-family: inherit; }
            .net-stat { display: flex; align-items: center; gap: 0.4em; color: $color-text-secondary; }
        }
    }

    .service-name {
        display: flex;
        flex-direction: column;
        gap: 0.25em;
        .service-id { font-size: 0.75em; color: $color-disabled-text; }
    }

    .service-badge {
        padding: 0.25em 0.75em;
        border-radius: 100px;
        font-size: 0.7em;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        background-color: rgba($color-danger, 0.15);
        color: $color-danger;
        &.running { background-color: rgba($color-success, 0.15); color: $color-success; }
    }
</style>
